import {
  ChatMessage,
  ConversationGoals,
  ConversationItem,
  RateLimit,
  SuggestionItem,
  ViaQuestion
} from '@cpd-app/core-module/models';
import { createAction, props } from '@ngrx/store';

// Chat with bot actions
export const newChat = createAction('[AiChat] New Chat');

// Get conversation history actions
export const getConversationHistory = createAction(
  '[AiChat] Get Conversation History',
  props<{ conversationId: number; limit: number; offset: number }>()
);

export const getConversationHistorySuccess = createAction(
  '[AiChat] Get Conversation History Success',
  props<{ messages: ChatMessage[] }>()
);

export const getConversationHistoryFailure = createAction(
  '[AiChat] Get Conversation History Failure',
  props<{ error: string }>()
);

// Load more conversations actions
export const loadConversations = createAction(
  '[AiChat] Load Conversations',
  props<{ offset: number; limit?: number }>()
);

export const loadConversationsSuccess = createAction(
  '[AiChat] Load Conversations Success',
  props<{ conversations: ConversationItem[]; conversationsCount: number }>()
);

export const loadConversationsFailure = createAction(
  '[AiChat] Load Conversations Failure',
  props<{ error: string }>()
);

// UI state actions
export const setCurrentConversation = createAction(
  '[AiChat] Set Current Conversation',
  props<{ conversationId: number | null }>()
);

export const clearMessages = createAction('[AiChat] Clear Messages');

export const setLoading = createAction(
  '[AiChat] Set Loading',
  props<{ isLoading: boolean }>()
);

// Delete conversation actions
export const deleteConversation = createAction(
  '[AiChat] Delete Conversation',
  props<{ conversationId: number }>()
);

export const deleteConversationSuccess = createAction(
  '[AiChat] Delete Conversation Success',
  props<{ conversationId: number }>()
);

export const deleteConversationFailure = createAction(
  '[AiChat] Delete Conversation Failure',
  props<{ error: string }>()
);

export const resetAiChatState = createAction('[AiChat] Reset Ai Chat State');

// Stream actions
export const streamStart = createAction(
  '[AiChat] Stream Start',
  props<{ query: string; question?: ViaQuestion }>()
);

export const streamConnected = createAction('[AiChat] Stream Connected');

export const streamChunk = createAction(
  '[AiChat] Stream Chunk',
  props<{ fullResponse: string }>()
);

export const streamComplete = createAction(
  '[AiChat] Stream Complete',
  props<{
    response: string;
    messageId: number;
    goals?: ConversationGoals;
    successSaveGoal?: boolean;
    courses?: SuggestionItem[];
    certificates?: SuggestionItem[];
  }>()
);

export const streamFailure = createAction(
  '[AiChat] Stream Failure',
  props<{ error: string }>()
);

export const askQuestion = createAction(
  '[AiChat] Ask Question',
  props<{ question: ViaQuestion }>()
);

/**
 * `auto` marks an open nobody asked for (the occupation page's timer). The
 * panel then leaves focus where the user is and only announces itself, since
 * moving focus without a request breaks WCAG 2.4.3 / 3.2.1 (A11Y.1, 28 Sep).
 * Every existing `openChatVia()` call is a user's click and stays unchanged.
 */
export const openChatVia = createAction(
  '[AiChat] Open Chat Via',
  (options: { auto?: boolean } = {}) =>
    options.auto ? { auto: true as const } : ({} as { auto?: true })
);

/**
 * T.8. A message from Via shown in the chat without a model call, for a page
 * that opens Via with something to say (the resume builder's opener). It is
 * not saved: the page that said it sends it along as context instead.
 */
export const viaSays = createAction(
  '[AiChat] Via Says',
  props<{ content: string }>()
);

export const closeChatVia = createAction('[AiChat] Close Chat Via');

// Rate limit actions
export const getRateLimit = createAction('[AiChat] Get Rate Limit');

export const getRateLimitSuccess = createAction(
  '[AiChat] Get Rate Limit Success',
  props<RateLimit>()
);

export const getRateLimitFailure = createAction(
  '[AiChat] Get Rate Limit Failure',
  props<{ error: string }>()
);

export const startRateLimitCountdown = createAction(
  '[AiChat] Start Rate Limit Countdown',
  props<{ resetTime: string }>()
);

export const updateTimeUntilReset = createAction(
  '[AiChat] Update Time Until Reset',
  props<{ timeUntilReset: string }>()
);

// Like message actions
export const likeMessage = createAction(
  '[AiChat] Like Message',
  props<{ messageId: number; liked: boolean }>()
);

export const dislikeMessage = createAction(
  '[AiChat] Dislike Message',
  props<{ messageId: number; disliked: boolean }>()
);

export const rateMessageSuccess = createAction(
  '[AiChat] Rate Message Success',
  props<{
    messageId: number;
    liked: boolean | null;
    disliked: boolean | null;
  }>()
);

export const rateMessageFailure = createAction(
  '[AiChat] Rate Message Failure',
  props<{ error: string }>()
);
